import { useTranslation } from 'react-i18next';

// Domain
import { getSidebarFilterOperator } from '../../../domain/calendarSidebarFilters/calendarSidebarFiltersUseCase';
import { isMultiValueType } from '../../../domain/tableConditionalFilter/conditionalFiltersUseCase';

// Components
import Button from '../../atoms/Button/Button';
import {
  CalendarSidebarSection,
  CalendarSidebarField,
} from '../../molecules/CalendarSidebar/CalendarSidebar';
import ConditionalFiltersRender from '../FilterConditionalFilters/ConditionalFiltersRender';

// Styles
import styles from './FilterCalendarSidebarFilters.module.css';

/**
 * FilterCalendarSidebarFilters - Timeline sidebar filters as label + field
 * @param {object} props
 * @param {ReturnType<typeof import('../../../domain/calendarSidebarFilters/useCalendarSidebarFilters').default>} props.sidebarFilters
 * @param {(data: Array<ApiManagerFilterValues>) => void} props.onSubmit
 * @param {string} [props.className]
 * @returns {JSX.Element | null}
 */
function FilterCalendarSidebarFilters({
  sidebarFilters: sidebarFiltersState,
  onSubmit,
  className = '',
}) {
  const { t } = useTranslation();

  const {
    sidebarFiltersGrouped,
    sidebarFiltersById,
    formSidebarFilters,
    buildSubmitPayload,
  } = sidebarFiltersState;

  const {
    control,
    handleSubmit,
  } = formSidebarFilters;

  if (!sidebarFiltersGrouped.length) {
    return null;
  }

  return (
    <form
      id="calendar-sidebar-filters-form"
      className={`${styles.form} ${className}`}
      onSubmit={handleSubmit((
        /** @type {{ conditionalFilters: ApiManagerGlobalFiltersCreateValue[] }} */ data,
      ) => {
        onSubmit(buildSubmitPayload(data.conditionalFilters));
      })}
    >
      {sidebarFiltersGrouped.map((group) => (
        <CalendarSidebarSection
          key={group.lib || 'default'}
          label={group.lib || t('table.filters.others')}
        >
          {group.options.map(({ filter, index }) => (
            <CalendarSidebarField key={filter.id} label={filter.lib}>
              <div className={styles['field-control']}>
                <ConditionalFiltersRender
                  controlRhf={control}
                  field={{
                    id: filter.id,
                    operator: getSidebarFilterOperator(filter),
                    values: [],
                    singleValue: !isMultiValueType(filter.type),
                  }}
                  name={`conditionalFilters.${index}.values`}
                  filtersById={sidebarFiltersById}
                  isClearable={!filter.required}
                />
              </div>
            </CalendarSidebarField>
          ))}
        </CalendarSidebarSection>
      ))}
    </form>
  );
}

/**
 * FilterCalendarSidebarFiltersFooter - Actions pinned to the bottom of the calendar sidebar
 * @param {object} props
 * @param {ReturnType<typeof import('../../../domain/calendarSidebarFilters/useCalendarSidebarFilters').default>} props.sidebarFilters
 * @param {boolean} props.isLoading
 * @param {boolean} props.isSubmitting
 * @returns {JSX.Element | null}
 */
export function FilterCalendarSidebarFiltersFooter({
  sidebarFilters: sidebarFiltersState,
  isLoading,
  isSubmitting,
}) {
  const { t } = useTranslation();
  const {
    sidebarFiltersGrouped,
    handleResetSidebarFilters,
    handleCancelSidebarFilters,
  } = sidebarFiltersState;

  if (!sidebarFiltersGrouped.length) {
    return null;
  }

  return (
    <div className={styles.footer}>
      <Button
        color="no-background"
        icon="refresh"
        size="small"
        iconPosition="right"
        onClick={handleResetSidebarFilters}
      >
        {t('calendar.sidebar.filters.reset')}
      </Button>
      <div className={styles['footer-actions']}>
        <Button
          color="no-background"
          size="small"
          onClick={handleCancelSidebarFilters}
        >
          {t('table.filters.cancel')}
        </Button>
        <Button
          size="small"
          isSubmit
          form="calendar-sidebar-filters-form"
          isLoading={isLoading || isSubmitting}
        >
          {t('table.filters.apply')}
        </Button>
      </div>
    </div>
  );
}

export default FilterCalendarSidebarFilters;
